import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewEncapsulation,
  SimpleChanges,
} from '@angular/core';
import { WindowService } from 'src/shared/providers/window.service';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import {
  ClickAction,
  ClickTargets,
  EventTypes,
  Period,
  Properties,
  Job,
} from 'src/models/schedule-weekly-view.model';
import { DateTime as LuxonDateTime } from 'luxon';
import { TitleCasePipe } from '@angular/common';

@Component({
    templateUrl: 'schedule-list-view.html',
    styleUrls: ['./schedule-list-view.scss'],
    selector: 'tidy-schedule-list-view',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ScheduleListViewComponent implements OnChanges {
  @Input() isMobileResolution: boolean;
  @Input() selectedPeriodRange: Period;
  @Input() properties: Properties;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onClickAction = new EventEmitter<ClickAction>();
  @Output() requestMoreData = new EventEmitter<{startDate: string, endDate: string}>();

  shownRows: any[] = [];
  allRows: any[] = [];
  headers: string[] = [];
  totalRows = 0;
  pageSize = 50;
  pageIndex = 1;
  batchSize = 3; // Load data in 3-day batches
  currentBatchStartDate: string;
  currentBatchEndDate: string;
  isLoadingMore = false;
  allDataLoaded = false;
  originalPeriodRange: Period; // Store the original full period range

  constructor(
    public windowService: WindowService,
    public onboardingProvider: OnboardingProvider
  ) {
    this.headers = [
      'Service',
      'Date',
      'Time',
      'Property',
      'Status',
      'Pro',
      'last-column'
    ];
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (this.properties) {
      this.initializeBatchLoading();
    }
  }

  initializeBatchLoading(): void {
    // Store the original period range
    this.originalPeriodRange = { ...this.selectedPeriodRange };

    // Reset all data
    this.shownRows = [];
    this.allRows = [];
    this.totalRows = 0;
    this.pageIndex = 1;
    this.isLoadingMore = false;
    this.allDataLoaded = true; // Initial load covers the full selected range

    // Set initial batch to the full selected period range
    this.currentBatchStartDate = this.selectedPeriodRange.startDate;
    this.currentBatchEndDate = this.selectedPeriodRange.endDate;

    // Load first batch (which now covers the entire selected period)
    this.loadCurrentBatch();
  }

  loadCurrentBatch(): void {
    const rows = this.transformDataForBatch();
    this.allRows = rows;
    this.shownRows = [...this.allRows];
    this.totalRows = this.allRows.length;
  }

  transformDataForBatch(): any[] {
    const rows = [];

    const titleCase = new TitleCasePipe();

    for (const property of this.properties) {
      const jobsToProcess = property.filteredJobs || property.jobs || [];
      const reservationsToProcess = property.filteredReservations || property.reservations || [];
      const propertyName = String(property.name || property.address || 'N/A');

      for (const reservation of reservationsToProcess) {
        const startDate = reservation.startDate ? LuxonDateTime.fromISO(reservation.startDate) : null;
        const nightsLabel = reservation.nights ? `${reservation.nights}n` : '';
        const dateRange = reservation.title || '';
        const dateValue = nightsLabel && dateRange
          ? `${nightsLabel} • ${dateRange}`
          : (nightsLabel || dateRange || (startDate ? startDate.toFormat('EEE, MMM d') : 'N/A'));
        const serviceLabel = reservation.guestName
          ? `Reservation • ${reservation.guestName}`
          : 'Reservation';

        rows.push({
          'Service': {
            value: serviceLabel
          },
          'Date': {
            value: dateValue
          },
          'Time': {
            value: reservation.checkInTime && reservation.checkOutTime
              ? `${reservation.checkInTime} - ${reservation.checkOutTime}`
              : '-'
          },
          'Property': {
            value: titleCase.transform(propertyName)
          },
          'Status': {
            value: this.getReservationStatusLabel(reservation)
          },
          'Pro': {
            value: reservation.sourceName || '-'
          },
          'last-column': {
            value: '',
            hasChevron: true
          },
          originalData: {
            reservation,
            property
          }
        });
      }

      for (const job of jobsToProcess) {
        const jobDate = job.startDate ? LuxonDateTime.fromISO(job.startDate) : null;

        rows.push({
          'Service': {
            value: titleCase.transform(String(job.service_name || job.serviceCategory || job.title || 'Cleaning'))
          },
          'Date': {
            value: jobDate ? jobDate.toFormat('EEE, MMM d') : 'N/A'
          },
          'Time': {
            value: job.time || 'N/A'
          },
          'Property': {
            value: titleCase.transform(propertyName)
          },
          'Status': {
            value: titleCase.transform(this.getJobStatus(job))
          },
          'Pro': {
            value: job.homekeeperNames || job.homekeeperName || '-'
          },
          'last-column': {
            value: '',
            hasChevron: true
          },
          originalData: {
            job,
            property
          }
        });
      }

      const availRequestsToProcess = property.filteredAvailabilityRequests || property.availabilityRequests || [];
      for (const request of availRequestsToProcess) {
        const reqDate = request.startDate ? LuxonDateTime.fromISO(request.startDate) : null;
        const statusValue = typeof request.status === 'string' ? request.status : request.status?.name || 'Pending';

        rows.push({
          'Service': {
            value: 'Availability Request'
          },
          'Date': {
            value: reqDate ? reqDate.toFormat('EEE, MMM d') : 'N/A'
          },
          'Time': {
            value: request.parsedRequestTimes || 'N/A'
          },
          'Property': {
            value: titleCase.transform(propertyName)
          },
          'Status': {
            value: titleCase.transform(statusValue)
          },
          'Pro': {
            value: '-'
          },
          'last-column': {
            value: '',
            hasChevron: true
          },
          originalData: {
            availabilityRequest: request,
            property
          }
        });
      }
    }

    // Sort by date (most recent first)
    rows.sort((a, b) => {
      const dateA = this.extractDate(a.originalData);
      const dateB = this.extractDate(b.originalData);
      if (!dateA && !dateB) {return 0;}
      if (!dateA) {return 1;}
      if (!dateB) {return -1;}
      return dateB.toMillis() - dateA.toMillis();
    });

    return rows;
  }

  extractDate(data: any): LuxonDateTime | null {
    if (data.job && data.job.startDate) {
      return LuxonDateTime.fromISO(data.job.startDate);
    }
    if (data.availabilityRequest && data.availabilityRequest.startDate) {
      return LuxonDateTime.fromISO(data.availabilityRequest.startDate);
    }
    if (data.reservation && data.reservation.startDate) {
      return LuxonDateTime.fromISO(data.reservation.startDate);
    }
    return null;
  }

  getReservationStatusLabel(reservation: any): string {
    switch (reservation?.state) {
      case 'inquiry':   return reservation.firstResponseAt ? 'Inquiry' : 'Needs response';
      case 'quote':     return 'Quote sent';
      case 'hold':      return 'Hold';
      case 'cancelled': return 'Cancelled';
      case 'no_show':   return 'No show';
      default: break;
    }
    const today = LuxonDateTime.now().startOf('day');
    const start = reservation?.startDate ? LuxonDateTime.fromISO(reservation.startDate).startOf('day') : null;
    const end   = reservation?.endDate   ? LuxonDateTime.fromISO(reservation.endDate).startOf('day')   : null;
    if (start?.hasSame(today, 'day')) {return 'Checking in';}
    if (end?.hasSame(today, 'day'))   {return 'Checking out';}
    if (start && end && start < today && end > today) {return 'In progress';}
    if (end && end < today) {return 'Past';}
    return 'Upcoming';
  }

  getJobStatus(job: Job): string {
    if (job.status) {
      if (typeof job.status === 'string') {
        return job.status;
      } else if (job.status.name) {
        return job.status.name;
      }
    }
    return 'Scheduled';
  }

  onTableColumnClick(event: any): void {
    // Get the data from wherever it is
    let data = null;
    if (event.element?.originalData) {
      data = event.element.originalData;
    } else if (event.row?.originalData) {
      data = event.row.originalData;
    } else if (event.element) {
      // Maybe originalData is directly on the element
      data = event.element;
    }

    if (!data) {
      return;
    }

    // Check if originalData is nested
    if (data.originalData) {
      data = data.originalData;
    }

    if (data.job) {
      const clickEvent: ClickAction = {
        target: 'job' as ClickTargets,
        eventType: 'jobs' as EventTypes,
        property: data.property,
        event: data.job,
      };
      this.onClickAction.emit(clickEvent);
    } else if (data.availabilityRequest) {
      const clickEvent: ClickAction = {
        target: 'availabilityRequest' as ClickTargets,
        eventType: 'availabilityRequests' as EventTypes,
        property: data.property,
        event: data.availabilityRequest,
      };
      this.onClickAction.emit(clickEvent);
    } else if (data.reservation) {
      const clickEvent: ClickAction = {
        target: 'event' as ClickTargets,
        eventType: 'reservations' as EventTypes,
        property: data.property,
        event: data.reservation,
      };
      this.onClickAction.emit(clickEvent);
    }
  }

  sortChanged(event: any): void {
    const sortColumn = event.active;
    const sortDirection = event.direction;

    if (!sortDirection) {
      // If no sort direction, reset to original order
      this.shownRows = [...this.allRows];
      return;
    }

    this.shownRows = [...this.shownRows].sort((a, b) => {
      const aValue = a[sortColumn]?.value || '';
      const bValue = b[sortColumn]?.value || '';

      // Handle date sorting specially
      if (sortColumn === 'Date') {
        const dateA = this.extractDate(a.originalData);
        const dateB = this.extractDate(b.originalData);
        if (!dateA && !dateB) {return 0;}
        if (!dateA) {return sortDirection === 'asc' ? 1 : -1;}
        if (!dateB) {return sortDirection === 'asc' ? -1 : 1;}
        const comparison = dateA.toMillis() - dateB.toMillis();
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Regular string/number comparison
      let comparison = 0;
      if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  pageChange(_event: any): void {
    this.loadNextBatch();
  }

  loadNextBatch(): void {
    if (this.isLoadingMore || this.allDataLoaded) {return;}

    const currentStart = LuxonDateTime.fromISO(this.currentBatchStartDate);
    const rangeStart = LuxonDateTime.fromISO(this.originalPeriodRange.startDate);

    if (currentStart <= rangeStart) {
      this.allDataLoaded = true;
      return;
    }

    this.isLoadingMore = true;

    // Calculate previous 3-day batch date range (going backwards in time)
    const previousEndDate = currentStart.minus({ days: 1 });
    let previousStartDate = previousEndDate.minus({ days: this.batchSize - 1 });

    // Clamp to the original period range boundary
    if (previousStartDate < rangeStart) {
      previousStartDate = rangeStart;
    }

    // Update current batch dates to the previous range
    this.currentBatchStartDate = previousStartDate.toISODate();
    this.currentBatchEndDate = previousEndDate.toISODate();

    // Request more data from parent component
    this.requestMoreData.emit({
      startDate: this.currentBatchStartDate,
      endDate: this.currentBatchEndDate
    });
  }

  trackByFn(index, _item) {
    return index;
  }
}
